'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import ConversionArea, { ConversionAreaProps } from './conversion-area';
import CtaButton, { CtaButtonProps } from './cta-button';
import CaseReviewCta, { CaseReviewCtaProps } from './case-review-cta';
import InquiryCta, { InquiryCtaProps } from './inquiry-cta';
import CtaWithInfo, { CtaWithInfoProps } from './cta-with-info';

/**
 * 순번을 세는 컴포넌트 목록. 본문에 같은 컴포넌트가 여러 번 삽입될 때 gtag label 을
 * `_1`·`_2` 로 나누기 위한 것이라 종류별로 0 부터 센다.
 *
 * 이름과 컴포넌트를 한 선언에 묶어 둔다 — 따로 두면 이름만 추가하고 export 를
 * 빠뜨렸을 때 컴파일 에러 없이 죽은 카운터만 남는다. 새 컴포넌트는 여기 한 줄과
 * 아래 export 한 줄만 추가하면 된다.
 */
const INDEXED_COMPONENTS = {
  conversionArea: ConversionArea,
  ctaButton: CtaButton,
  caseReviewCta: CaseReviewCta,
  inquiryCta: InquiryCta,
  ctaWithInfo: CtaWithInfo,
} as const;

type CounterName = keyof typeof INDEXED_COMPONENTS;

type GetNextIndex = (name: CounterName) => number;

const WordpressComponentContext = createContext<GetNextIndex | null>(null);

const createCounters = () =>
  Object.fromEntries(
    (Object.keys(INDEXED_COMPONENTS) as CounterName[]).map(
      (name): [CounterName, number] => [name, 0],
    ),
  ) as Record<CounterName, number>;

export function WordpressComponentWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const counters = useRef(createCounters());

  // 렌더 중에 증가시킨다 — MDX 는 본문에 나타난 순서대로 렌더되므로 그 순서가
  // 곧 삽입 순번이다.
  //
  // 개발 모드에서는 StrictMode 이중 렌더 때문에 순번이 2 씩 뛴다(`_2`·`_4`·`_6`).
  // 프로덕션 빌드는 한 번만 렌더해 `_1`·`_2`·`_3` 이 나오므로 GA 데이터는 정상이다
  // (2026-08-11 프로덕션 빌드로 실측). dev 에서 이상해 보여도 고치려 들지 말 것.
  const getNextIndex = useCallback<GetNextIndex>(
    name => counters.current[name]++,
    [],
  );

  // 언마운트 시 되돌린다. 남겨 두면 다음 글에서 순번이 이어져 label 이 `_1` 부터
  // 시작하지 않는다.
  useEffect(
    () => () => {
      counters.current = createCounters();
    },
    [],
  );

  return (
    <WordpressComponentContext.Provider value={getNextIndex}>
      {children}
    </WordpressComponentContext.Provider>
  );
}

/**
 * 삽입 순번을 주입하는 래퍼를 만든다.
 *
 * MDX 컴포넌트 맵에는 `index` 없는 형태로 넘겨야 하므로(작성자가 순번을 쓰지
 * 않는다) 래퍼가 컨텍스트에서 받아 채운다.
 */
function withIndex<P extends { index: number }>(
  Component: React.ComponentType<P>,
  name: CounterName,
) {
  function Indexed(props: Omit<P, 'index'>) {
    const getNextIndex = useContext(WordpressComponentContext);
    if (!getNextIndex) {
      throw new Error(
        'Indexed 컴포넌트는 WordpressComponentWrapper 안에서만 렌더할 수 있다',
      );
    }

    // `Omit<P, 'index'> & { index: number }` 가 `P` 라는 걸 TS 가 증명하지 못해
    // 단언이 필요하다 — index 는 스프레드 뒤에 오므로 항상 여기 값으로 덮인다.
    return <Component {...(props as P)} index={getNextIndex(name)} />;
  }

  Indexed.displayName = `Indexed(${Component.displayName ?? Component.name})`;
  return Indexed;
}

// 컴포넌트는 위 맵에서 꺼낸다 — 카운터 이름과 같은 키를 쓰므로 짝이 어긋나면 눈에 보인다
export const IndexedConversionArea = withIndex<ConversionAreaProps>(
  INDEXED_COMPONENTS.conversionArea,
  'conversionArea',
);
export const IndexedCtaButton = withIndex<CtaButtonProps>(
  INDEXED_COMPONENTS.ctaButton,
  'ctaButton',
);
export const IndexedCaseReviewCta = withIndex<CaseReviewCtaProps>(
  INDEXED_COMPONENTS.caseReviewCta,
  'caseReviewCta',
);
export const IndexedInquiryCta = withIndex<InquiryCtaProps>(
  INDEXED_COMPONENTS.inquiryCta,
  'inquiryCta',
);
export const IndexedCtaWithInfo = withIndex<CtaWithInfoProps>(
  INDEXED_COMPONENTS.ctaWithInfo,
  'ctaWithInfo',
);
